import PropTypes from "prop-types";
import styles from "./Input.module.css";

/**
 * Standard input component following project design patterns.
 * Uses 1.5rem font size and standard green borders.
 *
 * @example
 * // Basic text input
 * <Input label="Email" type="email" value={email} onChange={setEmail} />
 *
 * @example
 * // Password input with error
 * <Input
 *   label="Password"
 *   type="password"
 *   value={password}
 *   onChange={setPassword}
 *   error="Password is required"
 * />
 *
 * @example
 * // Textarea
 * <Input
 *   label="Description"
 *   type="textarea"
 *   value={description}
 *   onChange={setDescription}
 *   rows={5}
 * />
 */
const Input = ({
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  disabled = false,
  required = false,
  error,
  id,
  name,
  className = "",
  rows = 3,
  ...props
}) => {
  const inputId = id || name || label?.toLowerCase().replace(/\s+/g, "-");
  const isTextarea = type === "textarea";

  const handleChange = (e) => {
    onChange(e.target.value);
  };

  const inputClassName = `${styles.input} ${className}`.trim();

  return (
    <div className={styles.inputGroup}>
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
          {required && <span className={styles.required}> *</span>}
        </label>
      )}
      {isTextarea ? (
        <textarea
          id={inputId}
          name={name}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className={inputClassName}
          rows={rows}
          {...props}
        />
      ) : (
        <input
          id={inputId}
          name={name}
          type={type}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className={inputClassName}
          {...props}
        />
      )}
      {error && <span className={styles.error}>{error}</span>}
    </div>
  );
};

Input.propTypes = {
  label: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  className: PropTypes.string,
  rows: PropTypes.number,
};

export default Input;
